import React, { PropsWithChildren, useCallback, useState } from "react";

import { Button } from "@app/scripts/react-general-components/Button";
import { Portal } from "@app/scripts/react-general-components/Portal";

type Props = {
  heading: string;
  secondaryHeading?: string;
  opensFrom?: "left" | "right" | "bottom";
  customButton?: React.FunctionComponent<{
    toggleOpen: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    open: boolean;
  }>;
  drawerHeaderButton?: React.FunctionComponent;
  drawerClassName?: string;
  destroyOnHide?: boolean;
  preventScroll?: boolean;
  inline?: boolean;
  disableOverlay?: boolean;
};

export const Drawer: React.FC<PropsWithChildren<Props>> = (props) => {
  const {
    heading,
    customButton,
    destroyOnHide = true,
    preventScroll = true,
    inline = false,
    opensFrom,
    drawerClassName,
    drawerHeaderButton,
  } = props;

  const [open, setOpen] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(false);

  const toggleOpen = useCallback(
    (e?: React.MouseEvent<HTMLElement, MouseEvent>, _open?: boolean) => {
      e?.preventDefault();

      if (_open === false || !open) {
        setTimeout(() => setActive(true), 50);
        setOpen(true);
        if (preventScroll) document.body.classList.add("no-scroll");
      } else if (_open === true || open) {
        setActive(false);
        setTimeout(() => setOpen(false), 200);
        if (preventScroll && active === true) document.body.classList.remove("no-scroll");
      }
    },
    [open, active, setOpen, setActive]
  );

  return (
    <>
      {customButton ? (
        customButton({ toggleOpen, open })
      ) : (
        <Button
          color="outline"
          variant="secondary"
          className="fullwidth swiper-no-swiping"
          ariaLabel={heading}
          onClick={toggleOpen}
          label={heading}
        />
      )}

      {!open && destroyOnHide ? null : inline ? (
        <div
          className={`ui-drawer__inline ${drawerClassName ?? ""} ${active ? "active" : ""} from--${
            opensFrom ?? "right"
          }`}
        >
          <DrawerContent
            {...props}
            open={open}
            toggleOpen={toggleOpen}
            active={active}
            drawerHeaderButton={drawerHeaderButton}
          />
        </div>
      ) : (
        <Portal>
          <DrawerContent
            {...props}
            open={open}
            toggleOpen={toggleOpen}
            active={active}
            drawerHeaderButton={drawerHeaderButton}
          />
        </Portal>
      )}
    </>
  );
};

type DrawerContentProps = Props & {
  toggleOpen: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  open: boolean;
  active: boolean;
};

const DrawerContent: React.FC<PropsWithChildren<DrawerContentProps>> = ({
  drawerClassName,
  opensFrom,
  heading,
  secondaryHeading,
  toggleOpen,
  open,
  active,
  drawerHeaderButton,
  disableOverlay,
  children,
}) => (
  <>
    {open && !disableOverlay ? (
      <div className={`ui-drawer__overlay ${drawerClassName ?? ""}`} onClick={toggleOpen} />
    ) : null}

    <div className={`ui-drawer ${active ? "active" : ""} from--${opensFrom ?? "right"} ${drawerClassName ?? ""}`}>
      <div className="ui-drawer__heading page-width text-dark-grey">
        <div className="ui-drawer__heading-title-wrap">
          <h2 className="text-lg font-medium uppercase tracking-wider h3">
            {heading} {secondaryHeading && <span>{secondaryHeading}</span>}
          </h2>
          {drawerHeaderButton?.({})}
        </div>
        <button className="ui-drawer__close" onClick={toggleOpen}>
          <span className="button button--text hidden ui-drawer__button-text !font-light">Close</span>
          <ui-icon width="16" height="16" name="icon-close-large"></ui-icon>
        </button>
      </div>

      {React.Children.map(children, (child) => {
        return React.cloneElement(child as React.ReactElement, { toggleOpen });
      })}
    </div>
  </>
);
